import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, Clock3, LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AuthLayout } from "@/features/auth/components/AuthLayout";
import {
  loadEmailVerification,
  type EmailVerificationState,
} from "@/features/auth/email-verification";

export const Route = createFileRoute("/verify-email")({
  loader: loadEmailVerification,
  shouldReload: false,
  pendingComponent: VerificationPending,
  pendingMs: 0,
  component: VerifyEmailPage,
});

const contentKeys = {
  missing: {
    title: "auth.verifyEmail.missingTitle",
    subtitle: "auth.verifyEmail.missingSubtitle",
  },
  verified: {
    title: "auth.verifyEmail.verifiedTitle",
    subtitle: "auth.verifyEmail.verifiedSubtitle",
  },
  invalid: {
    title: "auth.verifyEmail.invalidTitle",
    subtitle: "auth.verifyEmail.invalidSubtitle",
  },
  expired: {
    title: "auth.verifyEmail.expiredTitle",
    subtitle: "auth.verifyEmail.expiredSubtitle",
  },
  used: {
    title: "auth.verifyEmail.usedTitle",
    subtitle: "auth.verifyEmail.usedSubtitle",
  },
  superseded: {
    title: "auth.verifyEmail.supersededTitle",
    subtitle: "auth.verifyEmail.supersededSubtitle",
  },
  failed: {
    title: "auth.verifyEmail.failedTitle",
    subtitle: "auth.verifyEmail.failedSubtitle",
  },
} as const satisfies Record<EmailVerificationState, { title: string; subtitle: string }>;

export function VerificationPending() {
  const { t } = useTranslation();

  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-4 py-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo/10">
          <LoaderCircle size={32} className="animate-spin text-indigo" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-bold text-heading">{t("auth.verifyEmail.loadingTitle")}</h1>
        <p className="text-sm text-muted">{t("auth.verifyEmail.loadingSubtitle")}</p>
      </div>
    </AuthLayout>
  );
}

function VerifyEmailPage() {
  const state = Route.useLoaderData();

  return <EmailVerificationResult state={state} />;
}

export function EmailVerificationResult({ state }: { state: EmailVerificationState }) {
  const { t } = useTranslation();
  const content = contentKeys[state];
  const Icon = state === "verified" ? CheckCircle : state === "expired" ? Clock3 : AlertCircle;
  const iconClass = state === "verified" ? "text-success" : "text-warning";
  const iconBackground = state === "verified" ? "bg-success/10" : "bg-warning/10";

  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-4 py-4 text-center">
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-full ${iconBackground}`}
        >
          <Icon size={32} className={iconClass} aria-hidden="true" />
        </div>
        <div role="alert" className="flex flex-col items-center gap-4">
          <h1 className="text-xl font-bold text-heading">{t(content.title)}</h1>
          <p className="text-sm text-muted">{t(content.subtitle)}</p>
        </div>
        <Link
          to="/login"
          className="mt-4 inline-flex h-10 items-center justify-center rounded-lg bg-indigo px-6 text-sm font-semibold text-white transition-all hover:bg-indigo-bright hover:shadow-lg hover:shadow-indigo/25 active:scale-[0.98]"
        >
          {t("auth.verifyEmail.backToLogin")}
        </Link>
      </div>
    </AuthLayout>
  );
}
